import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import InsightsIcon from '@mui/icons-material/Insights';
import { useSessionStore } from '@/stores/useSessionStore';
import { useInsightStore } from '@/stores/useInsightStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { changePassword } from '@/api/auth';

interface TopBarProps {
  height: number;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
}

/**
 * 顶部导航栏（56px 高）。
 * 包含 Logo、当前会话标题、设置和洞察按钮。
 */
const TopBar: React.FC<TopBarProps> = ({
  height,
  sidebarOpen,
  onToggleSidebar,
}) => {
  const navigate = useNavigate();
  const currentSession = useSessionStore((s) => s.currentSession);
  const { isOpen: insightOpen, togglePanel } = useInsightStore();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [passwordOpen, setPasswordOpen] = React.useState(false);
  const [oldPassword, setOldPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');

  const savePassword = async () => {
    await changePassword(oldPassword, newPassword);
    setPasswordOpen(false);
    setOldPassword('');
    setNewPassword('');
  };

  return (
    <AppBar
      position="relative"
      elevation={0}
      sx={{
        height,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          height,
          minHeight: height,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* 左侧：汉堡菜单 + Logo + 标题 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!sidebarOpen && (
            <Tooltip title="展开侧边栏">
              <IconButton
                edge="start"
                color="inherit"
                onClick={onToggleSidebar}
                size="small"
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onClick={() => navigate('/chat')}
          >
            深流
          </Typography>
          {currentSession && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                ml: 1.5,
                maxWidth: 300,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {currentSession.title}
            </Typography>
          )}
        </Box>

        {/* 右侧：设置 + 洞察 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="配置中心">
            <IconButton
              color="inherit"
              onClick={() => navigate('/config')}
              size="small"
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={insightOpen ? '关闭洞察' : '打开洞察'}>
            <IconButton
              color={insightOpen ? 'primary' : 'inherit'}
              onClick={togglePanel}
              size="small"
            >
              <InsightsIcon />
            </IconButton>
          </Tooltip>
          <Button size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: 'text.secondary' }}>
            {user?.display_name || user?.username || '用户'}
          </Button>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem onClick={() => { setPasswordOpen(true); setAnchorEl(null); }}>修改密码</MenuItem>
            <MenuItem onClick={() => { logout(); navigate('/login'); }}>退出登录</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <Dialog open={passwordOpen} onClose={() => setPasswordOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>修改密码</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" label="原密码" type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth size="small" label="新密码" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setPasswordOpen(false)}>取消</Button>
            <Button variant="contained" onClick={savePassword} disabled={!oldPassword || newPassword.length < 8}>保存</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </AppBar>
  );
};

export default TopBar;
